import React, { useState, useEffect } from 'react';
import { authFetch, isInteractiveAuthError } from '../authFetch';

// Consistent styling with UploadTelemetryModal
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalContentStyle = {
  backgroundColor: '#fff',
  padding: '25px 30px',
  border: '1px solid #222',
  borderRadius: '0px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  width: '420px',
  textAlign: 'left',
  fontSize: '0.8rem',
};

const selectStyle = {
  width: '100%',
  padding: '8px 10px',
  marginBottom: '12px',
  border: '1px solid #000',
  borderRadius: '0px',
  fontSize: '0.85rem',
  backgroundColor: '#fff',
};

const buttonStyle = {
  padding: '8px 18px',
  fontSize: '0.9rem',
  cursor: 'pointer',
  border: 'none',
  borderRadius: '0px',
  marginRight: '10px',
};

export default function UploadVideoModal({ open, defaultFlightUuid = '', flightOptions = [], onClose, onUploaded }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [flightUuid, setFlightUuid] = useState(defaultFlightUuid);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileName, setCurrentFileName] = useState('');

  // Keep internal UUID in sync if the parent passes a new default
  useEffect(() => {
    setFlightUuid(defaultFlightUuid);
  }, [defaultFlightUuid]);

  if (!open) return null;

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  const handleUpload = async () => {
    if (!flightUuid) {
      alert('Please select a flight before uploading videos.');
      return;
    }
    if (files.length === 0) {
      alert('Please choose at least one video file to upload.');
      return;
    }

    if (uploading) {
      return; // Prevent multiple uploads
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setCurrentFileName(file.name);
        
        // Update progress for current file start
        const baseProgress = (i / files.length) * 100;
        setUploadProgress(baseProgress);

        // Step 1: Get presigned URLs (20% of progress for this file)
        setUploadProgress(baseProgress + 5);
        const res = await authFetch("https://rkg7h3z4n9.execute-api.eu-west-2.amazonaws.com/presignVideoUpload-dev", {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            flight_uuid: flightUuid,
            files: [{ name: file.name, type: file.type }]
          })
        });
        
        if (!res.ok) throw new Error(`Failed to get upload URL for ${file.name}`);
        const { uploads } = await res.json();
        
        if (!uploads || uploads.length === 0) {
          throw new Error(`No upload URL received for ${file.name}`);
        }

        setUploadProgress(baseProgress + 15);

        // Step 2: Upload to S3 (60% of progress for this file)
        const upload = uploads[0];
        const uploadResponse = await fetch(upload.presigned_url, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        });
        
        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${file.name} to S3`);
        }

        setUploadProgress(baseProgress + 75);

        // Step 3: Complete upload (20% of progress for this file)
        await authFetch("https://s8edr9fzxa.execute-api.eu-west-2.amazonaws.com/completeVideoUpload-dev", {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            flight_uuid: flightUuid, 
            files: [upload]
          })
        });

        // Complete progress for this file
        setUploadProgress((i + 1) / files.length * 100);
        
        // Short delay to show completion
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      alert(`Successfully uploaded ${files.length} video file(s)!`);
      onUploaded();
      onClose();
    } catch (err) {
      if (isInteractiveAuthError(err)) return;
      console.error(err);
      alert(`Upload failed for ${currentFileName}: ${err.message}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setCurrentFileName('');
    }
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <h2 style={{ textAlign: 'center', marginBottom: 20, fontSize: '1rem' }}>Upload Video Files</h2>
        
        <label htmlFor="flightSelect">Attach to Flight:</label>
        <select 
          id="flightSelect"
          value={flightUuid} 
          onChange={e => setFlightUuid(e.target.value)} 
          style={selectStyle}
        >
          <option value="">-- Select Flight --</option>
          {flightOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <label htmlFor="videoFiles" style={{ display: 'block', marginBottom: '4px' }}>Video Files:</label>
        <input 
          id="videoFiles"
          type="file" 
          accept="video/*" 
          multiple 
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <label 
          htmlFor="videoFiles" 
          style={{ 
            ...buttonStyle, 
            backgroundColor: '#000', 
            color: '#fff', 
            display: 'inline-block', 
            marginBottom: '12px', 
            cursor: 'pointer' 
          }}
        >
          Choose Video File(s)
        </label>
        {files.length > 0 && (
          <div style={{ fontSize: '0.75rem', marginBottom: '12px' }}>
            {files.length} file(s) selected: {files.map(f => f.name).join(', ')}
          </div>
        )}

        {uploading && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '0.75rem', marginBottom: '4px' }}>
              Uploading {currentFileName}... ({Math.round(uploadProgress)}%)
            </div>
            <div style={{ 
              width: '100%', 
              backgroundColor: '#f0f0f0', 
              borderRadius: '4px', 
              overflow: 'hidden',
              height: '20px',
              border: '1px solid #ccc'
            }}>
              <div style={{ 
                width: `${uploadProgress}%`, 
                backgroundColor: '#000', 
                height: '100%',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>
        )}

        <div style={{ marginTop: 25, textAlign: 'right' }}>
          <button 
            onClick={onClose} 
            disabled={uploading}
            style={{ 
              ...buttonStyle, 
              backgroundColor: 'transparent', 
              color: uploading ? '#999' : '#000', 
              border: '1px solid #000',
              cursor: uploading ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button 
            onClick={handleUpload} 
            disabled={uploading || files.length === 0 || !flightUuid}
            style={{ 
              ...buttonStyle, 
              backgroundColor: (uploading || files.length === 0 || !flightUuid) ? '#666' : '#000', 
              color: '#fff',
              cursor: (uploading || files.length === 0 || !flightUuid) ? 'not-allowed' : 'pointer'
            }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
} 